import {
  IIPENSessionData,
  ISessionSigner,
  TUserTypes,
} from '../../types/IPENSessionData';
import { IOrder } from '../../types/Order';
import { IOrderDetails } from '../../types/OrderDetails';
import { ISigner, ISolexSigner } from '../../types/Parties';
import { OrderStatus } from '../constants/OrderStatus';
import { PushbackEvents } from '../constants/PushbackEvents';
import { UserRoles } from '../constants/roles';
import { SignerType, isSignerOfType } from '../constants/solexSignerTypes';
import { IAuthState } from '../redux-store/auth/authTypes';
import { IIPENSessionState } from '../redux-store/ipenSession/ipenSessionSlice';

type EnrichedSessionSigner = ISessionSigner & { hasDelegatedSigning: boolean };

/**
 * Aggregates order, IPEN state and auth data to drive an IPEN session
 * @param order
 * @param ipenSession
 * @param auth
 */
export const getIPENSessionData = (
  order: IOrder,
  ipenSession: IIPENSessionState,
  auth: IAuthState
): IIPENSessionData => {
  const sessionSigners: EnrichedSessionSigner[] =
    order?.OrderDetails?.Signers?.map(toSessionSigner()) ?? [];
  const allSignersSigned = sessionSigners.length
    ? sessionSigners
        .filter(
          (sessionSigner) =>
            !sessionSigner.hasDelegatedSigning &&
            !isSignerOfType(sessionSigner.signer, SignerType.Lender)
        )
        .every((sessionSigner) => sessionSigner.completed)
    : false;

  return {
    sessionActive: ipenSession?.isActiveSession,
    tagDocsLink: getTagDocumentsLink(order.OrderDetails),
    notary: order?.OrderDetails?.IPEN?.Notary ?? undefined,
    notaryLink: getDocLink(order.OrderDetails?.IPEN?.Notary?.SolexSigner),
    userType: getUserTypes(auth.currentUser.user_roles),
    sessionSigners,
    allSignersSigned,
  };
};

const toSessionSigner = (): ((signer: ISigner) => EnrichedSessionSigner) => {
  return (signer: ISigner) => {
    const hasCompletedSigning = !!signer.SolexSigner?.SignerEvents.find(
      (event) => event.Name === PushbackEvents.Sign
    );

    return {
      docLink: getDocLink(signer.SolexSigner),
      completed: hasCompletedSigning,
      sessionActive: false,
      linkDisabled: !!signer.SolexSigner?.IsSessionLocked,
      hasDelegatedSigning: !!signer.SigningDelegatePartyId,
      signer,
    };
  };
};

const getTagDocumentsLink = (orderDetails: IOrderDetails | undefined) => {
  const escrowOrSettlementTeamMembers: { SolexSigner?: ISolexSigner }[] = [
    ...(orderDetails?.EscrowTeam ?? []),
    ...(orderDetails?.SettlementTeam ?? []),
  ];

  const targetTeamMember = escrowOrSettlementTeamMembers.length
    ? escrowOrSettlementTeamMembers[0]
    : undefined;
  return getDocLink(targetTeamMember?.SolexSigner);
};

const getUserTypes = (userRolesString: string): TUserTypes[] => {
  const userRoles: object = JSON.parse(userRolesString);
  const userTypes: TUserTypes[] = [];

  Object.keys(userRoles).forEach((role) => {
    if (role === UserRoles.NOTARY_USERS_ROLE) {
      userTypes.push('notary');
    } else if (role === UserRoles.EOEA_USERS_ROLE) {
      userTypes.push('escrow');
    } else if (role === UserRoles.GLOBAL_USERS_ROLE) {
      userTypes.push('global');
    }
  });

  return userTypes;
};

const getDocLink = (solexSigner: ISolexSigner | undefined): string =>
  solexSigner?.SignerEvents.find(
    (event) => event.Name === OrderStatus.OpenLinksSent
  )?.DocLink ?? '';
